"use client";
import React, { type FormEvent, useCallback, useState } from "react";
import * as z from "zod";

const jobTitles = [
  "C-Suite",
  "Family Office",
  "Founder",
  "GP",
  "LP",
  "Government Role",
  "Marketing",
  "Wealth Management",
  "Philanthropist",
  "Other",
] as const;

const interests = [
  "Investment",
  "Philanthropy",
  "Portfolio Company",
  "Technology",
  "Media Inquiries",
  "Other",
] as const;

const getSortedArray = (
  arr: readonly string[],
): (string | { disabled: boolean; label: string; value: string })[] => {
  const sortedItems = arr.filter((item) => item !== "Other").sort((a, b) => a.localeCompare(b));

  return [...sortedItems, { disabled: true, label: "----", value: "" }, "Other"];
};

const getSortedJobTitles = (): (string | { disabled: boolean; label: string; value: string })[] =>
  getSortedArray(jobTitles);
const getSortedInterests = (): (string | { disabled: boolean; label: string; value: string })[] =>
  getSortedArray(interests);

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address."),
  interest: z.union([z.enum(interests), z.literal("Please select")]),
  isSubscribeToNewsletter: z.boolean().optional(),
  jobTitle: z.union([z.enum(jobTitles), z.literal("Please select")]),
  message: z.string().min(1, "Please enter a brief message."),
  name: z.string().min(1, "Please enter your name."),
  organization: z.string().optional(),
  telephoneNumber: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export default function JoinForm(): React.JSX.Element {
  const [formData, setFormData] = useState<FormData>({
    email: "",
    interest: "Please select",
    isSubscribeToNewsletter: false,
    jobTitle: "Please select",
    message: "",
    name: "",
    organization: "",
    telephoneNumber: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value, type } = event.target;
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? (event.target as HTMLInputElement).checked : value,
      }));
    },
    [],
  );

  const validateForm = useCallback(() => {
    try {
      formSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Partial<Record<keyof FormData, string>> = {};
        (error as z.ZodError<FormData>).issues.forEach((err: z.ZodIssue) => {
          if (err.path[0]) {
            newErrors[err.path[0] as keyof FormData] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  }, [formData]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!validateForm()) return;

      setIsSubmitting(true);
      try {
        const response = await fetch("/api/contact", {
          body: JSON.stringify(formData),
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
        });

        if (!response.ok) {
          throw new Error("Failed to submit form");
        }

        console.log("Form submitted successfully");
        setFormData({
          email: "",
          interest: "Please select",
          isSubscribeToNewsletter: false,
          jobTitle: "Please select",
          message: "",
          name: "",
          organization: "",
          telephoneNumber: "",
        });
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validateForm],
  );

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    void handleSubmit(event);
  };

  return (
    <form className="contact-form" onSubmit={onSubmit}>
      <div className="contact-form-cell">
        <label htmlFor="name">
          <em>Name*</em>
        </label>
        <input
          aria-invalid={!!errors.name}
          id="name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleInputChange}
        />
        {errors.name && <p role="alert">{errors.name}</p>}
      </div>

      <div className="contact-form-cell">
        <label htmlFor="email">
          <em>Email*</em>
        </label>
        <input
          aria-invalid={!!errors.email}
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
        />
        {errors.email && <p role="alert">{errors.email}</p>}
      </div>

      <div className="contact-form-cell">
        <label className="label-for-dropdown" htmlFor="jobTitle">
          <em>Job title</em>
          <select
            id="jobTitle"
            name="jobTitle"
            value={formData.jobTitle}
            onChange={handleInputChange}
          >
            <option value="Please select">Please select</option>
            {getSortedJobTitles().map((item, index) =>
              typeof item === "string" ? (
                <option key={item} value={item}>
                  {item}
                </option>
              ) : (
                <option disabled={item.disabled} key={`separator-${index}`} value={item.value}>
                  {item.label}
                </option>
              ),
            )}
          </select>
        </label>
        {errors.jobTitle && <p role="alert">{errors.jobTitle}</p>}
      </div>

      <div className="contact-form-cell">
        <label className="label-for-dropdown" htmlFor="interest">
          <em>Interest</em>
          <select
            id="interest"
            name="interest"
            value={formData.interest}
            onChange={handleInputChange}
          >
            <option value="Please select">Please select</option>
            {getSortedInterests().map((item, index) =>
              typeof item === "string" ? (
                <option key={item} value={item}>
                  {item}
                </option>
              ) : (
                <option disabled={item.disabled} key={`separator-${index}`} value={item.value}>
                  {item.label}
                </option>
              ),
            )}
          </select>
        </label>
        {errors.interest && <p role="alert">{errors.interest}</p>}
      </div>

      <div className="contact-form-cell">
        <label htmlFor="organization">
          <em>Organization</em>
        </label>
        <input
          id="organization"
          name="organization"
          type="text"
          value={formData.organization}
          onChange={handleInputChange}
        />
      </div>

      <div className="contact-form-cell">
        <label htmlFor="telephoneNumber">
          <em>Telephone</em>
        </label>
        <input
          id="telephoneNumber"
          name="telephoneNumber"
          type="tel"
          value={formData.telephoneNumber}
          onChange={handleInputChange}
        />
      </div>

      <div className="contact-form-cell contact-form-cell-message">
        <label htmlFor="message">
          <em>Message*</em>
        </label>
        <textarea
          aria-invalid={!!errors.message}
          id="message"
          name="message"
          value={formData.message}
          onChange={handleInputChange}
        />
        {errors.message && <p role="alert">{errors.message}</p>}
      </div>

      <div className="contact-form-cell contact-form-cell-subscribe">
        <label className="label-for-checkbox">
          <input
            checked={formData.isSubscribeToNewsletter}
            name="isSubscribeToNewsletter"
            type="checkbox"
            onChange={handleInputChange}
          />
          Subscribe to Newsletter
        </label>
      </div>

      <div className="contact-form-cell">
        <button
          data-is-submitting={isSubmitting ? "true" : "false"}
          disabled={isSubmitting}
          type="submit"
        >
          Submit
        </button>
      </div>
    </form>
  );
}
